import template from "./map.html";
import en from "./en.json";
import es from "./es.json";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

class MapController {
    /* @ngInject */
    constructor($element, $scope, $q) {
        this.$element = $element;
        this.$scope = $scope;
        this.$q = $q;
    }

    canRemoveLastPin() {
        let ctrl = this;
        return Array.isArray(ctrl.multiMarkers) && ctrl.multiMarkers.length > 0;
    }

    removeLastPin() {
        let ctrl = this;
        if (!ctrl.canRemoveLastPin()) {
            return;
        }

        let removedMarker = ctrl.multiMarkers.pop();
        if (removedMarker) {
            removedMarker.map = null;
        }

        if (
            Array.isArray(ctrl.multiMarkerDragEndListeners) &&
            ctrl.multiMarkerDragEndListeners.length
        ) {
            let removedListener = ctrl.multiMarkerDragEndListeners.pop();
            if (removedListener) {
                removedListener.remove();
            }
        }

        if (ctrl.emitMarkersUpdate) {
            ctrl.emitMarkersUpdate();
        }
    }

    $onInit() {
        let ctrl = this;

        setOptions({
            key: process.env.GOOGLE_PUBLIC_API_KEY,
            v: "weekly",
            libraries: ["maps", "core", "marker"],
        });

        let mapsLibrary = importLibrary("maps");
        let markerLibrary = importLibrary("marker");
        let coreLibrary = importLibrary("core");
        ctrl.$q
            .all([mapsLibrary, markerLibrary, coreLibrary])
            .then(function (libs) {
                let [maps, marker, core] = libs;
                let mapElement = ctrl.$element.find("div")[0];
                if (!mapElement) {
                    return;
                }

                ctrl.map = new maps.Map(mapElement, {
                    zoom: 15,
                    mapTypeId: "hybrid",
                    mapId: "DEMO_MAP_ID",
                    fullscreenControl: false,
                    streetViewControl: false,
                    controlSize: 20,
                });

                function normalizeLatLng(position) {
                    if (position === null || position === undefined) {
                        return null;
                    }

                    if (
                        typeof position.lat === "function" &&
                        typeof position.lng === "function"
                    ) {
                        return position;
                    }

                    let lat = Number(position.lat);
                    let lng = Number(position.lng);

                    if (Number.isNaN(lat) || Number.isNaN(lng)) {
                        return null;
                    }

                    return new core.LatLng(lat, lng);
                }

                function setMapCenter(position) {
                    let normalizedPosition = normalizeLatLng(position);
                    if (!normalizedPosition) {
                        return;
                    }

                    ctrl.map.setCenter(normalizedPosition);
                }

                function watchCenterPosition() {
                    ctrl.centerLatLngWatch = ctrl.$scope.$watchGroup(
                        ["$ctrl.centerLat", "$ctrl.centerLng"],
                        function (newValues, oldValues) {
                            let [lat, lng] = newValues;

                            if (lat === null || lat === undefined) {
                                return;
                            }

                            if (lng === null || lng === undefined) {
                                return;
                            }

                            if (
                                oldValues &&
                                oldValues[0] === lat &&
                                oldValues[1] === lng
                            ) {
                                return;
                            }

                            setMapCenter({ lat: lat, lng: lng });
                        },
                    );
                }

                function emitMarkerUpdate(position) {
                    if (!ctrl.onMarkerUpdate) {
                        return;
                    }

                    ctrl.$scope.$evalAsync(function () {
                        ctrl.onMarkerUpdate({ position: position });
                    });
                }

                function normalizeMarkerPosition(position) {
                    let normalizedPosition = normalizeLatLng(position);
                    if (!normalizedPosition) {
                        return null;
                    }

                    return {
                        lat: Number(normalizedPosition.lat().toFixed(8)),
                        lng: Number(normalizedPosition.lng().toFixed(8)),
                    };
                }

                function emitMarkersUpdate() {
                    if (!ctrl.multiMarkers) {
                        return;
                    }

                    let positions = ctrl.multiMarkers
                        .map(function (mapMarker) {
                            return normalizeMarkerPosition(mapMarker.position);
                        })
                        .filter(function (position) {
                            return !!position;
                        });

                    ctrl.$scope.$evalAsync(function () {
                        ctrl.markerPositions = positions;

                        if (!ctrl.onMarkersUpdate) {
                            return;
                        }

                        ctrl.onMarkersUpdate({ positions: positions });
                    });
                }

                ctrl.emitMarkersUpdate = emitMarkersUpdate;

                function setMarkerPosition(position, shouldEmitUpdate) {
                    let normalizedPosition = normalizeLatLng(position);
                    if (!ctrl.marker || !normalizedPosition) {
                        return;
                    }

                    ctrl.marker.position = normalizedPosition;
                    ctrl.map.panTo(normalizedPosition);

                    if (shouldEmitUpdate) {
                        emitMarkerUpdate(normalizedPosition);
                    }
                }

                function ensureMarker(draggable) {
                    if (!ctrl.marker) {
                        ctrl.marker = new marker.AdvancedMarkerElement({
                            map: ctrl.map,
                            gmpDraggable: draggable,
                        });
                    } else {
                        ctrl.marker.gmpDraggable = draggable;
                    }

                    if (!ctrl.markerDragEndListener) {
                        ctrl.markerDragEndListener = ctrl.marker.addListener(
                            "dragend",
                            function () {
                                setMarkerPosition(ctrl.marker.position, true);
                            },
                        );
                    }
                }

                function addMultiMarker(position, shouldEmitUpdate) {
                    let normalizedPosition = normalizeLatLng(position);
                    if (!normalizedPosition) {
                        return;
                    }

                    let mapMarker = new marker.AdvancedMarkerElement({
                        map: ctrl.map,
                        position: normalizedPosition,
                        gmpDraggable: !ctrl.static,
                    });

                    ctrl.multiMarkers.push(mapMarker);
                    ctrl.multiMarkerDragEndListeners.push(
                        mapMarker.addListener("dragend", function () {
                            emitMarkersUpdate();
                        }),
                    );

                    if (shouldEmitUpdate !== false) {
                        emitMarkersUpdate();
                    }
                }

                ctrl.multiMarkers = [];
                ctrl.multiMarkerDragEndListeners = [];
                let shouldUseMultiMarkers = Array.isArray(ctrl.markerPositions);

                if (shouldUseMultiMarkers) {
                    let centerPosition = {
                        lat: ctrl.centerLat,
                        lng: ctrl.centerLng,
                    };

                    if (ctrl.markerPositions.length) {
                        ctrl.markerPositions.forEach(function (position) {
                            addMultiMarker(position, false);
                        });
                        emitMarkersUpdate();
                        centerPosition = ctrl.multiMarkers[0].position;
                    }

                    setMapCenter(centerPosition);

                    if (!ctrl.static) {
                        ctrl.mapClickListener = ctrl.map.addListener(
                            "click",
                            function (e) {
                                addMultiMarker(e.latLng, true);
                            },
                        );
                    }
                } else {
                    let centerPosition;
                    if (
                        ctrl.markerLat === null ||
                        ctrl.markerLat === undefined ||
                        ctrl.markerLng === null ||
                        ctrl.markerLng === undefined
                    ) {
                        centerPosition = {
                            lat: ctrl.centerLat,
                            lng: ctrl.centerLng,
                        };

                        if (!ctrl.static) {
                            ctrl.mapClickListener = ctrl.map.addListener(
                                "click",
                                function (e) {
                                    if (ctrl.marker) {
                                        return;
                                    }

                                    if (ctrl.mapClickListener) {
                                        ctrl.mapClickListener.remove();
                                        ctrl.mapClickListener = null;
                                    }

                                    ensureMarker(true);
                                    setMarkerPosition(e.latLng, true);
                                },
                            );
                        }
                    } else {
                        centerPosition = {
                            lat: ctrl.markerLat,
                            lng: ctrl.markerLng,
                        };
                        ensureMarker(!ctrl.static);
                        setMarkerPosition(centerPosition, true);
                    }

                    setMapCenter(centerPosition);

                    if (!ctrl.static) {
                        ctrl.markerLatLngWatch = ctrl.$scope.$watchGroup(
                            ["$ctrl.markerLat", "$ctrl.markerLng"],
                            function (newValues, oldValues) {
                                let [lat, lng] = newValues;

                                if (lat === null || lat === undefined) {
                                    return;
                                }

                                if (lng === null || lng === undefined) {
                                    return;
                                }

                                if (
                                    oldValues &&
                                    oldValues[0] === lat &&
                                    oldValues[1] === lng
                                ) {
                                    return;
                                }

                                ensureMarker(true);
                                setMarkerPosition({ lat: lat, lng: lng }, true);
                            },
                        );
                    }
                }

                watchCenterPosition();
            });
    }

    $onDestroy() {
        let ctrl = this;

        if (ctrl.mapClickListener) {
            ctrl.mapClickListener.remove();
            ctrl.mapClickListener = null;
        }

        if (ctrl.markerDragEndListener) {
            ctrl.markerDragEndListener.remove();
            ctrl.markerDragEndListener = null;
        }

        if (ctrl.markerLatLngWatch) {
            ctrl.markerLatLngWatch();
            ctrl.markerLatLngWatch = null;
        }

        if (ctrl.centerLatLngWatch) {
            ctrl.centerLatLngWatch();
            ctrl.centerLatLngWatch = null;
        }

        if (ctrl.multiMarkerDragEndListeners) {
            ctrl.multiMarkerDragEndListeners.forEach(function (listener) {
                listener.remove();
            });
            ctrl.multiMarkerDragEndListeners = null;
        }

        if (ctrl.multiMarkers) {
            ctrl.multiMarkers.forEach(function (mapMarker) {
                mapMarker.map = null;
            });
            ctrl.multiMarkers = null;
        }
    }
}
export default {
    bindings: {
        markerLat: "=",
        markerLng: "=",
        markerPositions: "=",
        centerLat: "<",
        centerLng: "<",
        heightClass: "@?",
        static: "<",
        onMarkerUpdate: "&",
        onMarkersUpdate: "&?",
    },
    controller: MapController,
    template: template,
    name: "dynamicMap",
    translations: { en, es },
};
